import { useCallback, useEffect, useRef, useState } from 'react'
import { formularioService } from '@/services/formularioService'
import {
  EtapaFormulario,
  FormularioReserva,
  MarcoExigencia,
  PendenciaFormulario,
  RespostaFormulario,
  temFormulario,
} from '@/types/formulario'
import { respostasParaEnvio } from '@/utils/formularioCondicao'

export type EstadoFormularioReserva = 'inativo' | 'carregando' | 'ok' | 'erro'

export interface UseFormularioReserva {
  estado: EstadoFormularioReserva
  /** Última resposta do servidor (GET ou PUT). */
  formulario: FormularioReserva | null
  /** A etapa tem formulário com itens (falso também enquanto carrega/erro). */
  temFormulario: boolean
  /** Respostas locais (editadas e ainda não gravadas quando `sujo`). */
  respostas: RespostaFormulario[]
  alterar: (respostas: RespostaFormulario[]) => void
  /** Há edição local ainda não gravada — as `pendencias` do servidor podem estar velhas. */
  sujo: boolean
  salvando: boolean
  /**
   * Grava as respostas (PUT) e devolve o estado novo do servidor, com as pendências
   * recalculadas. Sem formulário, não chama nada e devolve o estado atual. Lança o erro
   * da API (409 `FORMULARIO_VERSAO_DIVERGENTE`, 400 `FORMULARIO_RESPOSTA_INVALIDA`…).
   */
  salvar: () => Promise<FormularioReserva | null>
  recarregar: () => Promise<void>
}

/** Pendências do servidor até o marco pedido (os marcos são cumulativos). */
export const pendenciasAte = (
  pendencias: PendenciaFormulario[] | null | undefined,
  marco: MarcoExigencia
): PendenciaFormulario[] =>
  (pendencias ?? []).filter((p) => marco === 'ENCAMINHAMENTO_PARCEIRO' || p.marco === 'ENVIO_ANALISE')

/**
 * Estado do formulário dinâmico de UMA etapa da reserva:
 * `GET/PUT /v1/oportunidades/{id}/formulario?etapa=`. `reservaId` nulo = inativo (ex.: a
 * reserva ainda não foi criada). O front não decide obrigatoriedade: usa as `pendencias`
 * devolvidas pelo servidor.
 */
export const useFormularioReserva = (
  reservaId: string | null | undefined,
  etapa: EtapaFormulario
): UseFormularioReserva => {
  const [estado, setEstado] = useState<EstadoFormularioReserva>(reservaId ? 'carregando' : 'inativo')
  const [formulario, setFormulario] = useState<FormularioReserva | null>(null)
  const [respostas, setRespostas] = useState<RespostaFormulario[]>([])
  const [sujo, setSujo] = useState(false)
  const [salvando, setSalvando] = useState(false)
  /** Descarta respostas que chegam depois de trocar de reserva/etapa. */
  const geracao = useRef(0)

  const recarregar = useCallback(async () => {
    const minha = ++geracao.current
    if (!reservaId) {
      setEstado('inativo')
      setFormulario(null)
      setRespostas([])
      setSujo(false)
      return
    }
    setEstado('carregando')
    try {
      const f = await formularioService.obter(reservaId, etapa)
      if (minha !== geracao.current) return
      setFormulario(f)
      setRespostas(f?.respostas ?? [])
      setSujo(false)
      setEstado('ok')
    } catch {
      if (minha !== geracao.current) return
      setEstado('erro')
    }
  }, [reservaId, etapa])

  useEffect(() => {
    void recarregar()
  }, [recarregar])

  const alterar = useCallback((novas: RespostaFormulario[]) => {
    setRespostas(novas)
    setSujo(true)
  }, [])

  const salvar = useCallback(async (): Promise<FormularioReserva | null> => {
    if (!reservaId || !formulario || !temFormulario(formulario) || !formulario.schema) return formulario
    setSalvando(true)
    try {
      const novo = await formularioService.salvar(reservaId, etapa, {
        formularioId: formulario.formularioId as string,
        respostas: respostasParaEnvio(formulario.schema, respostas),
      })
      setFormulario(novo)
      setRespostas(novo?.respostas ?? [])
      setSujo(false)
      return novo
    } finally {
      setSalvando(false)
    }
  }, [reservaId, etapa, formulario, respostas])

  return {
    estado,
    formulario,
    temFormulario: estado === 'ok' && temFormulario(formulario),
    respostas,
    alterar,
    sujo,
    salvando,
    salvar,
    recarregar,
  }
}

export default useFormularioReserva
